// src/components/theme/ThemeSpecialIndicator.js

import React, { useRef, useState, useEffect } from 'react';
import { Group, Text, Rect } from 'react-konva';
import PropTypes from 'prop-types';
import CustomColorPicker from '../CustomColorPicker';
import { specialIndicatorUsageOptions } from '../ThemeLists'
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';

const scaleFactor = 2;

// Settings Component for Special Indicator
export const ThemeSpecialIndicatorSettings = ({ element, updateElement, openColorModal }) => {
    // Handler for size change
    const handleSizeChange = (e) => {
        const size = parseInt(e.target.value, 10);
        if (!isNaN(size) && size > 0) {
            updateElement(element.id, 'size', size);
        }
    };

    // Handler for usage select change
    const handleUsageChange = (e) => {
        updateElement(element.id, 'usage', e.target.value);
    };

    // Handlers for X and Y position changes
    const handleXChange = (e) => {
        const newX = parseInt(e.target.value, 10);
        if (!isNaN(newX)) {
            updateElement(element.id, 'x', newX);
        }
    };

    const handleYChange = (e) => {
        const newY = parseInt(e.target.value, 10);
        if (!isNaN(newY)) {
            updateElement(element.id, 'y', newY);
        }
    };

    return (
        <div>
            {/* X Position */}
            <label>
                X Position:
                <input
                    className={styles.inputNumber}
                    type="number"
                    min="0"
                    max={SCREEN_WIDTH - 1}
                    value={element.x}
                    onChange={handleXChange}
                    style={{ marginLeft: '10px' }}
                />
            </label>

            {/* Y Position */}
            <label>
                Y Position:
                <input
                    className={styles.inputNumber}
                    type="number"
                    min="0"
                    max={SCREEN_HEIGHT - 1}
                    value={element.y}
                    onChange={handleYChange}
                    style={{ marginLeft: '10px' }}
                />
            </label>

            {/* Font Size */}
            <label style={{ display: 'block', marginTop: '10px' }}>
                Font Size:
                <select
                    className={styles.select100}
                    value={element.size}
                    onChange={handleSizeChange}
                    style={{ marginLeft: '10px', width: '100px' }}
                >
                    <option value={10}>10</option>
                    <option value={14}>14</option>
                    <option value={18}>18</option>
                </select>
            </label>

            {/* Color */}
            <CustomColorPicker
                label="Color"
                color={element.color}
                onChange={(value) => { updateElement(element.id, 'color', value); }}
            />

            {/* Usage */}
            <label style={{ display: 'block', marginTop: '10px' }}>
                Usage:
                <select
                    className={styles.select100}
                    value={element.usage}
                    onChange={handleUsageChange}
                    style={{ marginLeft: '10px', width: '120px' }}
                >
                    {specialIndicatorUsageOptions.map((option) => (
                        <option key={`usage-${option.value}`} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </label>
        </div>
    );
};

ThemeSpecialIndicatorSettings.propTypes = {
    element: PropTypes.shape({
        id: PropTypes.string.isRequired,
        usage: PropTypes.string.isRequired,
        size: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
    }).isRequired,
    updateElement: PropTypes.func.isRequired,
    openColorModal: PropTypes.func.isRequired,
};

// Special Indicator Component
const ThemeSpecialIndicator = ({
    element,
    selectedElement,
    setSelectedElement,
    updateElement,
    render,editAllowed,
    scale,
    snapToGridEnabled,
    gridSize
}) => {
    const groupRef = useRef();
    const [displayText, setDisplayText] = useState('');
    const textRef = useRef(null);
    const [textDimensions, setTextDimensions] = useState({ width: 0, height: 0 });
    const [fontLoaded, setFontLoaded] = useState(false);


    // Determine label based on usage
    useEffect(() => {
        switch (String(element.usage)) {
            case '0':
                setDisplayText('M');
                break;
            case '1':
                setDisplayText('+');
                break;
            case '2':
                setDisplayText('X');
                break;
            default:
                setDisplayText('-');
        }
    }, [element.usage]);

    // Handle drag end
    const handleDragEnd = (e) => {
        const { x, y } = e.target.position();
        updateElement(element.id, 'x', x);
        updateElement(element.id, 'y', y);
    };

    useEffect(() => {
        if (document.fonts) {
            document.fonts.load(`10pt 'ForcedSquare'`).then(() => {
                setFontLoaded(true);
            });
        } else {
            setFontLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (fontLoaded && textRef.current) {
            const textWidth = textRef.current.width() - 2;
            const textHeight = textRef.current.height();
            setTextDimensions({ width: textWidth, height: textHeight });
        }
    }, [fontLoaded, displayText, element.size]);

    // Determine if this element is selected
    const isSelected = selectedElement && selectedElement.id === element.id;


    return (
        <Group
            x={element.x}
            y={element.y}
            draggable={!render && editAllowed}
            dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, 10, 10) }}
            onClick={() => setSelectedElement(element)}
            onDragEnd={handleDragEnd}
            ref={groupRef}
        >
            {fontLoaded && (
                <Text
                    ref={textRef}
                    text={displayText}
                    fontSize={element.size * scaleFactor}
                    fill={element.color}
                    fontFamily="'ForcedSquare', monospace"
                    draggable={false}
                />
            )}
            {!render && fontLoaded && textDimensions.width > 0 && textDimensions.height > 0 && (
                <Rect
                    x={0}
                    y={0}
                    width={textDimensions.width}
                    height={textDimensions.height}
                    stroke={"#1976d2"}
                    dash={isSelected ? [1, 1] : [0, 0]}
                    strokeWidth={0.5}
                    listening={false}
                />
            )}

        </Group>
    );
};

ThemeSpecialIndicator.propTypes = {
    element: PropTypes.shape({
        id: PropTypes.string.isRequired,
        usage: PropTypes.string.isRequired,
        size: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
    }).isRequired,
    selectedElement: PropTypes.object,
    setSelectedElement: PropTypes.func.isRequired,
    updateElement: PropTypes.func.isRequired,
    editAllowed: PropTypes.bool,
};

export default ThemeSpecialIndicator;
